import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { isOutingPast } from '../../../../utils/outingTiming';
import { isSailingOutingsEnabled } from '../../utils/moduleUtils';

function toDate(value) {
  return value?.toDate?.() || (value instanceof Date ? value : null);
}

function formatOuting(outing = {}) {
  const date = toDate(outing.date);
  const label = date ? date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : 'Date TBC';
  return `${label}${outing.fromTime ? ` · ${outing.fromTime}` : ''}`;
}

export default function MySailingOutingsCard({
  teamId,
  userId,
  modules = {},
  onOpenEvent,
}) {
  const enabled = isSailingOutingsEnabled(modules);
  const [outings, setOutings] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [filter, setFilter] = useState('upcoming');

  useEffect(() => {
    if (!teamId || !userId || !enabled) {
      setOutings([]);
      return undefined;
    }
    const outingsQuery = query(collection(db, 'teams', teamId, 'sailingOutings'), where('status', '==', 'submitted'));
    const unsubscribe = safeOnSnapshot(outingsQuery, (snapshot) => {
      setOutings(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    }, undefined, { label: 'sailing-member-outings', teamId });
    return () => unsubscribe();
  }, [enabled, teamId, userId]);

  useEffect(() => {
    if (!teamId || !userId || !enabled || outings.length === 0) {
      setAssignments([]);
      return undefined;
    }
    const crewsByOuting = new Map();
    const publish = () => {
      setAssignments(outings.map((outing) => ({ outing, crews: crewsByOuting.get(outing.id) || [] })).filter((row) => row.crews.length > 0));
    };
    const unsubscribes = outings.map((outing) => safeOnSnapshot(
      query(collection(db, 'teams', teamId, 'sailingOutings', outing.id, 'crews'), where('memberIds', 'array-contains', userId)),
      (snapshot) => {
        crewsByOuting.set(outing.id, snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        publish();
      },
      () => {
        crewsByOuting.set(outing.id, []);
        publish();
      }
    ));
    publish();
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [enabled, outings, teamId, userId]);

  const visibleAssignments = useMemo(() => assignments.filter(({ outing }) => {
    if (filter === 'all') return true;
    const past = isOutingPast(outing);
    return filter === 'past' ? past : !past;
  }), [assignments, filter]);

  if (!enabled || !teamId || !userId) return null;

  return (
    <div className="team-card">
      <div className="card-header-with-icon"><span className="card-title">My Club-led Outings</span></div>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['upcoming', 'all', 'past'].map((value) => (
            <button key={value} type="button" className={filter === value ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'} onClick={() => setFilter(value)}>
              {value.charAt(0).toUpperCase() + value.slice(1)}
            </button>
          ))}
        </div>
        {visibleAssignments.length === 0 ? <p className="text-muted">No club-led outings assigned to you in this range.</p> : (
          <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
            {visibleAssignments.map(({ outing, crews }) => (
              <div key={outing.id} className="card" style={{ padding: 12 }}>
                <strong>{outing.name || outing.linkedEventName || 'Sailing outing'}</strong>
                <div className="text-muted">{formatOuting(outing)}</div>
                {crews.map((crew) => <div key={crew.id} className="status-pill info" style={{ marginTop: 8 }}>{crew.selectedBoatName ? `Boat: ${crew.selectedBoatName}` : String(crew.status || 'pending')}</div>)}
                {outing.linkedEventId && <button type="button" className="btn btn--secondary btn--xs" style={{ marginTop: 8 }} onClick={() => onOpenEvent?.({ id: outing.linkedEventId, originalId: outing.linkedEventId })}>Open event</button>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
